/**
 * Moderation Topic Detail Page
 *
 * Displays detailed information about a specific moderation topic
 * with case management, moderator activity, and resolution tracking.
 */

import { useCurrentCommunityId, useCommunityDetail } from '@communities/community/hooks';
import { HeroBackground } from '@core/components/HeroBackground';
import { useTopicModerationStats } from '@moderation/hooks';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ModerationTopicDetailContent } from './components/ModerationTopicDetailContent';
import { ModerationTopicDetailLoadingState } from './components/ModerationTopicDetailLoadingState';
import { ModerationTopicNotFound } from './components/ModerationTopicNotFound';

/* eslint-disable complexity */
export function ModerationTopicDetailPage() {
  // Scroll to top when page loads for accessibility and standard web practice
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();
  const { topicId } = useParams<{ topicId: string }>();
  const communityId = useCurrentCommunityId();
  const { community, loading: communityLoading } = useCommunityDetail();

  // Convert topic ID from URL format (kebab-case) to display format
  const topicName = topicId
    ? topicId
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';

  // Fetch topic-specific moderation stats
  const { data: topicData, loading: topicLoading } = useTopicModerationStats(communityId || '', topicName);
  const topicStats = topicData?.topicModerationStats;

  const loading = communityLoading || topicLoading;

  // Transform API data to component format
  const topic = topicStats
    ? {
        id: topicId || '',
        name: topicStats.topicName,
        severity: 'MEDIUM' as const, // Severity will be provided by API in future
        cases: topicStats.openCasesCount + topicStats.assignedCasesCount,
        percentChange: 0, // Historical comparison will be provided by API in future
        isIncrease: false,
        moderators: [], // Moderator assignments will be provided by API in future
        colors: [], // Avatar colors will be provided by API in future
        description: `${topicStats.topicName} content submitted by community members for moderation review.`,
        openCases: topicStats.openCasesCount,
        inProgressCases: topicStats.assignedCasesCount,
        resolvedToday: topicStats.inSLACases, // Cases resolved within SLA
        averageResolutionTime: 'N/A', // Resolution time metrics will be provided by API in future
      }
    : null;

  // Early returns for different states
  if (loading) {
    return <ModerationTopicDetailLoadingState communityId={communityId || ''} topicId={topicId || ''} />;
  }

  if (!topic) {
    return <ModerationTopicNotFound onBackClick={() => navigate(-1)} />;
  }

  return (
    <div>
      <HeroBackground imageUrl={community?.superHeroArtUrl} />
      <ModerationTopicDetailContent community={community} communityId={communityId || ''} topic={topic} topicId={topicId || ''} />
    </div>
  );
}
